import { useState } from "react";
import { Paper, Typography, Box, Button } from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Loading from "../ui/Loading";
import CallsDetailModal from "./CallsDetailModal";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const UnbilledByClientChart = ({
  data,
  isLoading,
  error,
  filters,
  callsDetail,
  getCalls,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  if (isLoading) return <Loading />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!data || data.length === 0)
    return <Typography>No unbilled calls available</Typography>;

  // Show top 15 only
  const displayData = data.slice(0, 15);

  const labels = displayData.map((item) => item.clientName || "Unknown");
  const callCounts = displayData.map((item) => item.callCount || 0);
  const totalValues = displayData.map((item) => item.totalValue || 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: "Call Count",
        data: callCounts,
        backgroundColor: "rgba(153, 102, 255, 0.6)",
        borderColor: "rgba(153, 102, 255, 1)",
        borderWidth: 1,
        yAxisID: "y",
      },
      {
        label: "Total Value ($)",
        data: totalValues,
        backgroundColor: "rgba(255, 159, 64, 0.6)",
        borderColor: "rgba(255, 159, 64, 1)",
        borderWidth: 1,
        yAxisID: "y1",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Call Count",
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        title: {
          display: true,
          text: "Total Value ($)",
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              if (context.dataset.yAxisID === "y1") {
                label += "$" + context.parsed.y.toLocaleString();
              } else {
                label += context.parsed.y;
              }
            }
            return label;
          },
        },
      },
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const client = displayData[index];
        setSelectedClient(client);
        setShowModal(true);
      }
    },
  };

  return (
    <>
      <Paper sx={{ p: 2, height: "100%" }}>
        <Typography
          variant="h6"
          gutterBottom
        >
          Unbilled by Client (Top 15)
        </Typography>
        <Box sx={{ height: 400 }}>
          <Bar
            data={chartData}
            options={options}
          />
        </Box>
        {data.length > 15 && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowModal(true)}
            >
              View All ({data.length} clients)
            </Button>
          </Box>
        )}
      </Paper>

      {showModal && (
        <CallsDetailModal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedClient(null);
          }}
          filters={{ ...filters, unbilledOnly: true }}
          clientId={selectedClient?.clientId}
          clientName={selectedClient?.clientName}
          callsDetail={callsDetail}
          getCalls={getCalls}
        />
      )}
    </>
  );
};

export default UnbilledByClientChart;
